import React from "react";
import { styled } from "@mui/material/styles";
import {
  Icon,
  InputAdornment,
  TextField,
  type TextFieldProps,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import { VALIDATION_LIMITS } from "@/config/constants";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "var(--neutral-white)",
    color: (theme.vars || theme).palette.text.primary,
    borderRadius: "var(--radius-control)",
    height: "40px",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid var(--neutral-500)",
    },
    "& .MuiInputAdornment-root": {
      "& img": {
        filter: "none",
      },
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--neutral-600)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme.vars || theme).palette.primary.main,
      borderWidth: 2,
    },
    "&.Mui-disabled": {
      backgroundColor: (theme.vars || theme).palette.action.disabledBackground,
      "& .MuiInputAdornment-root": {
        "& img": {
          filter: "opacity(0.38)",
        },
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    "& input::placeholder": {
      color: (theme.vars || theme).palette.text.secondary,
      opacity: 1,
    },
  },
}));

// Custom number format component
type NumberFormatCustomProps = {
  onChange: (_event: { target: { value: string } }) => void;
  decimalScale?: number;
  maxValue?: number;
  value?: string | number;
  readOnly?: boolean;
  fixedDecimalScale?: boolean;
  [key: string]: any;
};

const NumberFormatCustom = React.forwardRef<
  HTMLInputElement,
  NumberFormatCustomProps
>((props, ref) => {
  const {
    onChange,
    decimalScale,
    maxValue = VALIDATION_LIMITS.MAX_NUMBER_FIELD_DEFAULT,
    value,
    readOnly,
    fixedDecimalScale = false,
    ...other
  } = props;

  return (
    <NumericFormat
      {...other}
      value={value}
      getInputRef={ref}
      readOnly={readOnly}
      onValueChange={(values) => {
        if (readOnly) return;
        if (values.value !== value) {
          onChange({ target: { value: values.value } });
        }
      }}
      thousandSeparator
      valueIsNumericString
      prefix="$"
      suffix=" USD"
      decimalScale={decimalScale}
      fixedDecimalScale={fixedDecimalScale}
      allowNegative={false}
      isAllowed={(values) => {
        const { value, floatValue } = values;
        const numValue = Number(value);

        if (readOnly && floatValue === 0) return true;
        if (floatValue === 0) return false;
        return (
          floatValue === undefined ||
          (decimalScale === 0
            ? Number.isInteger(numValue) && numValue <= maxValue
            : numValue <= maxValue)
        );
      }}
    />
  );
});

type RpCurrencyFieldProps = Omit<TextFieldProps, "variant" | "size"> & {
  fixedDecimalScale?: boolean;
  decimalScale?: number;
  maxValue?: number;
  icon?: string;
  readOnly?: boolean;
  smallSize?: boolean;
};

const RpCurrencyField = ({
  fixedDecimalScale = false,
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  placeholder = "$0.00 USD",
  decimalScale = 2,
  maxValue = VALIDATION_LIMITS.MAX_NUMBER_FIELD_DEFAULT,
  icon = "money1",
  readOnly = false,
  smallSize = false,
  sx,
  ...rest
}: RpCurrencyFieldProps) => {
  return (
    <StyledTextField
      {...rest}
      sx={[
        smallSize ? { "& input": { fontSize: "12px" } } : {},
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      variant="outlined"
      size="small"
      value={value}
      onChange={onChange}
      error={error}
      helperText={helperText}
      disabled={disabled}
      placeholder={placeholder}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Icon component={RpSvgIcon} name={icon} color="primary" />
            </InputAdornment>
          ),
          inputComponent: NumberFormatCustom,
          readOnly,
        },
        htmlInput: {
          decimalScale,
          maxValue,
          value, // Pass value to NumberFormatCustom
          readOnly, // Pass readOnly to NumberFormatCustom
          fixedDecimalScale,
        },
      }}
    />
  );
};

export default RpCurrencyField;
